/**
 * Doctor time-off, "needs attention" appointments, and the clinic's changes to an appointment (medopd-docs 87).
 *
 * The backend owns every rule: one doctor can't be booked twice at the same time (any clinic, any channel), time-off
 * blocks new bookings, and each change the patient should hear about is sent to them after it's saved. A refused
 * time comes back as HTTP 409 with errorCode SLOT_<reason> and a message fit to show as is.
 */
import { fetchApi } from '../client';

export type TimeOffHandling = 'CANCEL_AND_NOTIFY' | 'KEEP_AND_FLAG';

export interface TimeOff {
  id: string;
  doctorId: string;
  /** Local date-times, "yyyy-MM-ddTHH:mm[:ss]". */
  from: string;
  to: string;
  reason: string | null;
  /** TIME_OFF (added here) or SLOT_BLOCK / SCHEDULE_BLOCK (a blocked time from the older screens). */
  source: 'TIME_OFF' | 'SLOT_BLOCK' | 'SCHEDULE_BLOCK';
  createdBy: string | null;
  createdAt: string | null;
}

/** An appointment that falls inside a time-off period or outside new working hours. */
export interface AffectedAppointment {
  appointmentId: string;
  patientName: string;
  date: string;
  timeSlot: string | null;
  status: string;
  consultationMode: 'IN_PERSON' | 'VIDEO';
  tokenNumber: number | null;
}

export interface TimeOffCreated {
  timeOff: TimeOff;
  affected: AffectedAppointment[];
  cancelled: number;
  flagged: number;
}

export type AttentionReason = 'DOCTOR_UNAVAILABLE' | 'OUTSIDE_HOURS';

export interface NeedsAttention {
  appointmentId: string;
  doctorId: string;
  patientId: string;
  patientName: string;
  visitDate: string;
  timeSlot: string | null;
  status: 'REQUESTED' | 'WAITING';
  consultationMode: 'IN_PERSON' | 'VIDEO';
  tokenNumber: number | null;
  reason: AttentionReason | string;
  note: string | null;
  flaggedAt: string | null;
}

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });
const doctorPath = (doctorId: string) => `/api/v1/doctors/${encodeURIComponent(doctorId)}/time-off`;

export const bookingIntegrityModule = {
  timeOff: {
    list: (doctorId: string) => fetchApi<TimeOff[]>(doctorPath(doctorId)),
    /** Who is booked in that period, before choosing what to do with them. */
    preview: (doctorId: string, from: string, to: string) =>
      fetchApi<{ affected: AffectedAppointment[] }>(`${doctorPath(doctorId)}/preview`, json('POST', { from, to })),
    create: (doctorId: string, body: { from: string; to: string; reason?: string; handling: TimeOffHandling }) =>
      fetchApi<TimeOffCreated>(doctorPath(doctorId), json('POST', body)),
    remove: (doctorId: string, id: string) =>
      fetchApi<void>(`${doctorPath(doctorId)}/${encodeURIComponent(id)}`, json('DELETE')),
  },

  /** Upcoming appointments the front desk must look at; a doctor sees only their own. */
  needsAttention: (doctorId?: string) =>
    fetchApi<NeedsAttention[]>(`/api/appointments/needs-attention${doctorId ? `?doctorId=${encodeURIComponent(doctorId)}` : ''}`),

  /** Switch an upcoming appointment between in-clinic and video; the patient is told. */
  changeMode: (appointmentId: string, mode: 'IN_PERSON' | 'VIDEO') =>
    fetchApi<unknown>(`/api/appointments/${encodeURIComponent(appointmentId)}/consultation-mode`, json('PATCH', { mode })),

  /**
   * Move an appointment to a day + start (the end is the doctor's slot length that day); the patient is told.
   * {@code opts.doctorId}: another active doctor of this clinic (medopd-docs 136 round 2); {@code opts.walkIn}: today's
   * queue with no fixed time (no start).
   */
  reschedule: (appointmentId: string, newDate: string, newStartTime?: string, newEndTime?: string, reason?: string,
    opts?: { doctorId?: string; walkIn?: boolean }) =>
    fetchApi<unknown>(`/api/v1/appointments/${encodeURIComponent(appointmentId)}/reschedule`,
      json('PUT', { appointmentId, newDate, newStartTime, newEndTime, reason, doctorId: opts?.doctorId, walkIn: opts?.walkIn })),

  /** Clinic cancels; the patient is told (with the reason, when given, and a link to book again). */
  cancel: (appointmentId: string, reason?: string) =>
    fetchApi<unknown>(`/api/v1/appointments/${encodeURIComponent(appointmentId)}${reason?.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''}`,
      json('DELETE')),

  /** Keep a flagged appointment as booked (clears the flag; nothing changes for the patient). */
  keepAsBooked: (appointmentId: string) =>
    fetchApi<unknown>(`/api/appointments/${encodeURIComponent(appointmentId)}/needs-attention/resolve`, json('POST')),
};

/** True when the backend refused a time (taken, held, doctor away, too soon…): the time list should be refreshed. */
export const isSlotRefusal = (errorCode?: string | null) => !!errorCode && errorCode.startsWith('SLOT_');

/** "9:05", "09:05" or "09:05:00" → "09:05"; anything else (e.g. "N/A") → undefined, so the server picks the slot. */
export function toHHmm(t?: string | null): string | undefined {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t.trim()) : null;
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : undefined;
}
